// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

/**
 * @fileOverview
 * Defines a reducer that works with the `websockets` store state,
 * and handles all chat related actions.
 */

import { objectWithoutUndefined } from '../libraries/utils/object'
import { InvalidRequest } from '../error'
import { validateConversationsReducerData } from './chat/validations'
import {
  getConversationsByMissionIds,
  mergeConversations,
} from './chat/reducerUtils'

/**
 * Websocket/action structure for CONVERSATION:
 * {
 *   type: 'CHAT_CONVERSATIONS',
 *    data: {
 *    {
 *      id: <int>,
 *      name: <string>,
 *      lastReadTime: <int>,
 *      lastStaffReadTime: <int>,
 *      archived: <bool>,
 *      isCityBased: <bool>,
 *      ack: <string>,
 *    }
 *  }
 * }
 */
const conversationsReducer = (state, action) => {
  if (action.type !== 'CHAT_CONVERSATIONS') return state
  const { data, pageInfo } = action
  validateConversationsReducerData(data)

  const validConversationsIds = [
    ...state.validConversationsIds,
    ...data.map(({ id }) => id),
  ]

  const mergedConversations = mergeConversations({
    persistedConversations: state.conversations,
    newConversations: data,
    validConversationsIds,
  })

  const conversationsByMissionIds = getConversationsByMissionIds(
    mergedConversations,
  )

  return {
    ...state,
    conversations: mergedConversations,
    conversationsByMissionIds,
    validConversationsIds,
    conversationsPageInfo: pageInfo,
  }
}

/**
 * Websocket/action structure for CONVERSATION:
 * {
 *   type: 'CHAT_CONVERSATION',
 *   id: <int>,
 *   name: <string>,
 *   lastReadTime: <int>,
 *   lastStaffReadTime: <int>,
 *   archived: <bool>,
 *   isCityBased: <bool>,
 *   ack: <string>,
 * }
 */
const conversationReducer = (state, action) => {
  if (action.type !== 'CHAT_CONVERSATION') return state

  const {
    id,
    name,
    lastReadTime,
    lastStaffReadTime,
    archived,
    isCityBased,
    participantsIds,
    missionId,
  } = action
  if (
    typeof id !== 'number' || // Always required
    ['undefined', 'string'].indexOf(typeof name) === -1 ||
    ['undefined', 'number'].indexOf(typeof lastReadTime) === -1 ||
    ['undefined', 'number'].indexOf(typeof lastStaffReadTime) === -1 ||
    ['undefined', 'boolean'].indexOf(typeof archived) === -1 ||
    ['undefined', 'boolean'].indexOf(typeof isCityBased) === -1
  )
    throw new InvalidRequest('Invalid conversation data')

  return {
    ...state,
    conversations: {
      ...state.conversations,
      [id]: Object.assign(
        { orderedMessages: [] },
        state.conversations[id],
        objectWithoutUndefined({
          id,
          name,
          lastReadTime,
          archived,
          isCityBased,
          lastStaffReadTime,
          participantsIds,
          missionId,
        }),
      ),
    },
  }
}

/**
 * Websocket/action structure for MESSAGE:
 * {
 *   type: 'CHAT_MESSAGE',
 *   id: <int>,
 *   userId: <int>,
 *   conversationId: <int>,
 *   timestamp: <int>,
 *   payload: <json>,
 *   ack: <string>,
 * }
 */
const messageReducer = (state, action) => {
  if (action.type !== 'CHAT_MESSAGE') return state

  let { id, userId, conversationId, timestamp, payload } = action

  if (
    typeof id !== 'number' || // Always required
    ['undefined', 'number'].indexOf(typeof userId) === -1 ||
    ['undefined', 'number'].indexOf(typeof conversationId) === -1 ||
    ['undefined', 'number'].indexOf(typeof timestamp) === -1 ||
    ['undefined', 'object'].indexOf(typeof payload) === -1
  )
    throw new InvalidRequest('Invalid message data')

  const msg = state.messages[id]
  const isNew = msg === undefined
  if (!isNew) {
    // Prettier adds this semicolon, but the API repo's linter doesn't like it
    // eslint-disable-next-line no-extra-semi
    ;({ id, userId, conversationId, timestamp, payload } = Object.assign(
      {},
      msg,
      action,
    ))
  }

  if (!payload) throw new InvalidRequest('Cannot have message without payload')

  if (!timestamp)
    throw new InvalidRequest('Cannot have message without timestamp')

  const user =
    state.users[userId] === undefined ? { id: userId } : state.users[userId]
  const convWithUnorderedMsgs =
    state.conversations[conversationId] === undefined
      ? {
          id: conversationId,
          orderedMessages: [id],
          name:
            user && user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName.slice(0, 1)}`
              : '',
        }
      : state.conversations[conversationId]

  const msgNeedsOrdering =
    isNew ||
    (timestamp && state.messages[id].timestamp !== timestamp) ||
    !convWithUnorderedMsgs.orderedMessages.includes(id)

  const orderedMessages = msgNeedsOrdering
    ? insertMessageOrdered(
        state,
        convWithUnorderedMsgs.orderedMessages,
        id,
        timestamp,
      )
    : convWithUnorderedMsgs.orderedMessages

  const messages = {
    ...state.messages,
    [id]: Object.assign(
      {},
      state.messages[id],
      objectWithoutUndefined({
        id,
        userId,
        conversationId,
        timestamp,
        payload,
      }),
    ),
  }

  const isEndUser =
    convWithUnorderedMsgs.participantsIds &&
    convWithUnorderedMsgs.participantsIds.includes(String(user.id))

  const lastReadTimeProp = isEndUser ? 'lastReadTime' : 'lastStaffReadTime'

  const conv = isNew
    ? {
        ...convWithUnorderedMsgs,
        [lastReadTimeProp]: Date.now(),
        orderedMessages,
      }
    : {
        ...convWithUnorderedMsgs,
        orderedMessages,
      }

  const unorderedState = {
    ...state,
    conversations: {
      ...state.conversations,
      [conversationId]: {
        ...conv,
      },
    },
    messages,
    users: {
      ...state.users,
      [userId]: user,
    },
  }

  const convNeedsOrdering = msgNeedsOrdering
  const orderedConversations = convNeedsOrdering
    ? [conversationId, ...state.orderedConversations]
    : state.orderedConversations

  const conversations = {
    ...unorderedState.conversations,
    [conversationId]: {
      ...conv,
      orderedMessages,
    },
  }

  const conversationsByMissionIds = getConversationsByMissionIds(conversations)

  return {
    ...unorderedState,
    conversations,
    orderedConversations,
    conversationsByMissionIds,
  }
}

/**
 * Websocket/action structure for USER:
 * {
 *   type: 'CHAT_USER',
 *   id: <int>,
 *   firstName: <string>,
 *   lastName: <string>,
 *   ack: <string>,
 * }
 */
const userReducer = (state, action) => {
  if (action.type !== 'CHAT_USER') return state

  const { id, firstName, lastName, cityName, profilePicture } = action

  if (
    typeof id !== 'number' || // Always required
    ['undefined', 'string'].indexOf(typeof firstName) === -1 ||
    ['undefined', 'string'].indexOf(typeof lastName) === -1
  )
    throw new InvalidRequest('Invalid user data', action)

  return {
    ...state,
    users: {
      ...state.users,
      [id]: Object.assign(
        {},
        state.users[id],
        objectWithoutUndefined({
          id,
          firstName,
          lastName,
          cityName,
          profilePicture,
        }),
      ),
    },
  }
}

const clearAllChatData = (state, action) => {
  if (action.type !== 'CHAT_CLEAR_ALL_CHAT_DATA') return state

  return {
    connected: false,
    conversations: {},
    orderedConversations: [],
    validConversationsIds: [],
    messages: {},
    users: {},
    conversationsByMissionIds: {},
  }
}

/**
 * Returns a copy of `orderedMessages` with `id` inserted into it
 * and ordered considering the provided `timestamp` compared to other
 * messages as extracted from `state`. If `id` is already in
 * `orderedMessages`, then it will first be removed (to be reordered).
 *
 * Descending timestamp order (first is most recent).
 *
 * Assumptions:
 * - `state` is the entire websocket state,
 *   but can disclude the new conversation/message itself
 * - State contains all messages in `orderedMessages` (ignoring `id` itself)
 * - `orderedMessages` is already in order (ignoring `id` itself)
 */
const insertMessageOrdered = (state, orderedMessages, id, timestamp) => {
  const newList = [...orderedMessages]

  const i = newList.indexOf(id)
  if (i !== -1) newList.splice(i, 1)

  let insertAt = newList
    .map(_ => [_, state.messages[_]])
    .findIndex(([, msg]) => msg.timestamp < timestamp)
  if (insertAt === -1) insertAt = newList.length

  return [...newList.slice(0, insertAt), id, ...newList.slice(insertAt)]
}

const reducerChain = [
  conversationReducer,
  messageReducer,
  userReducer,
  conversationsReducer,
  clearAllChatData,
]

export default (state, action) =>
  reducerChain.reduce((newState, reducer) => reducer(newState, action), state)
